import { useCallback, useState } from "react";
import type { Selection } from "react-aria-components";

type UseCrossPageSelectionOptions<T, D> = {
  getKey: (item: T) => string;
  toData: (item: T) => D;
};

export const useCrossPageSelection = <T, D>({ getKey, toData }: UseCrossPageSelectionOptions<T, D>) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedData, setSelectedData] = useState<Map<string, D>>(new Map());
  const [isAllPagesSelected, setIsAllPagesSelected] = useState(false);

  const clear = useCallback(() => {
    setSelectedKeys([]);
    setSelectedData(new Map());
    setIsAllPagesSelected(false);
  }, []);

  const selectAllAcrossPages = useCallback(
    (items: T[]) => {
      const data = new Map<string, D>();
      for (const item of items) data.set(getKey(item), toData(item));
      setSelectedKeys([...data.keys()]);
      setSelectedData(data);
      setIsAllPagesSelected(true);
    },
    [getKey, toData],
  );

  const createSelectionHandler = useCallback(
    (currentPageItems: T[] | undefined) => (keys: Selection) => {
      const pageItems = currentPageItems ?? [];
      const pageKeys = pageItems.map(getKey);
      const pageKeySet = new Set(pageKeys);
      const selectedOnPage = keys === "all" ? pageKeySet : new Set([...keys] as string[]);

      if (pageKeys.some((key) => !selectedOnPage.has(key))) setIsAllPagesSelected(false);

      setSelectedKeys((prev) => [
        ...prev.filter((key) => !pageKeySet.has(key)),
        ...pageKeys.filter((key) => selectedOnPage.has(key)),
      ]);
      setSelectedData((prev) => {
        const next = new Map(prev);
        for (const item of pageItems) {
          const key = getKey(item);
          if (!selectedOnPage.has(key)) next.delete(key);
          else if (!next.has(key)) next.set(key, toData(item));
        }
        return next;
      });
    },
    [getKey, toData],
  );

  return {
    selectedKeys,
    selectedData,
    isAllPagesSelected,
    selectedCount: selectedKeys.length,
    clear,
    selectAllAcrossPages,
    createSelectionHandler,
  };
};
